import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, MessageCircleQuestion } from "lucide-react";
import { EXPRESSION_CHECK_URL, expressionCheckPath, trackBookingCta, OPEN_COACHING_GUIDE_EVENT } from "@/lib/marketingBooking";
import { isMarketingHost } from "@/lib/marketingHost";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export type SiteLang = "en" | "de";
export type SitePage = "home" | "program" | "stories" | "podcast" | "insights" | "profiles";

export const CLIENT_LOGIN_URL = "https://app.philsconversation.ch/auth";
export const CALENDLY_URL = EXPRESSION_CHECK_URL;

export const SITE_PATHS: Record<SitePage, Record<SiteLang, string>> = {
  home: { en: "/", de: "/de" },
  program: { en: "/pure-practice", de: "/de/pure-practice" },
  stories: { en: "/client-stories", de: "/de/client-stories" },
  podcast: { en: "/podcast", de: "/podcast" },
  insights: { en: "/coaching-insights", de: "/de/coaching-insights" },
  profiles: { en: "/your-expression-gap", de: "/de/your-expression-gap" },
};

/** Link target for a page: canonical paths on the public domain, "/start" elsewhere (where "/" is the client dashboard). */
export function sitePath(page: SitePage, lang: SiteLang): string {
  if (page === "home" && !isMarketingHost()) return lang === "de" ? "/de/start" : "/start";
  return SITE_PATHS[page][lang];
}

const SUB_PATHS = {
  precision: { en: "/your-expression-gap/speak-with-precision", de: "/de/your-expression-gap/speak-with-precision" },
  confidence: { en: "/your-expression-gap/speak-with-confidence", de: "/de/your-expression-gap/speak-with-confidence" },
  quiz: { en: "/expression-quiz", de: "/de/expression-quiz" },
} as const;

const LABELS = {
  en: { home: "Home", program: "1:1 Coaching", stories: "Client Stories", podcast: "Podcast", insights: "Coaching Insights", profiles: "Expression Gap", read: "Insights & Podcast", overview: "Overview", precision: "The Free Speaker", confidence: "The Careful Speaker", quiz: "Or take the Expression Gap Quiz", book: "Let's talk", bookNote: "15 minutes · No sales pressure", menu: "Menu", login: "Client login" },
  de: { home: "Start", program: "1:1 Coaching", stories: "Kundenstimmen", podcast: "Podcast", insights: "Coaching Insights", profiles: "Expression Gap", read: "Insights & Podcast", overview: "Übersicht", precision: "The Free Speaker", confidence: "The Careful Speaker", quiz: "Oder mach das Expression Gap Quiz", book: "Lass uns sprechen", bookNote: "15 Min. · Ohne Verkaufsdruck", menu: "Menü", login: "Kunden-Login" },
} as const;

interface SiteNavProps {
  lang: SiteLang;
  current: SitePage;
  onLangChange?: (lang: SiteLang) => void;
}

export function SiteNav({ lang, current, onLangChange }: SiteNavProps) {
  const [open, setOpen] = useState(false);
  const l = LABELS[lang];
  const pressable = "transition-transform duration-150 active:scale-[0.98] motion-reduce:transition-none motion-reduce:active:scale-100";
  const deskCls = (active: boolean) => `site-nav-link relative inline-flex items-center gap-1 py-2 text-sm font-medium transition-colors ${active ? "is-active text-primary" : "text-muted-foreground hover:text-foreground"}`;

  const deskLink = (page: SitePage) => (
    <Link key={page} to={sitePath(page, lang)} aria-current={page === current ? "page" : undefined} className={deskCls(page === current)}>
      <span className="site-nav-label">{l[page]}</span>
    </Link>
  );

  const deskGroup = (label: string, active: boolean, items: { to: string; label: string; muted?: boolean }[]) => (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className={`${deskCls(active)} outline-none`}>
        <span className="site-nav-label">{label}</span><ChevronDown className="h-3.5 w-3.5" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {items.map((it, i) => (
          <div key={it.to}>
            {it.muted && i > 0 && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild className={`cursor-pointer py-2 ${it.muted ? "text-muted-foreground" : "font-medium"}`}><Link to={it.to}>{it.label}</Link></DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const gapItems = [
    { to: SITE_PATHS.profiles[lang], label: l.overview },
    { to: SUB_PATHS.precision[lang], label: l.precision },
    { to: SUB_PATHS.confidence[lang], label: l.confidence },
    { to: SUB_PATHS.quiz[lang], label: l.quiz, muted: true },
  ];
  const readItems = [
    { to: sitePath("insights", lang), label: l.insights },
    { to: sitePath("podcast", lang), label: l.podcast },
  ];

  const mLink = (to: string, label: string, active = false, sub = false) => (
    <Link key={to} to={to} onClick={() => setOpen(false)} aria-current={active ? "page" : undefined}
      className={`site-nav-drawer-link block rounded-lg px-3 ${sub ? "py-2 pl-6 text-base font-medium" : "py-3 text-lg font-bold"} ${active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"}`}>{label}</Link>
  );

  const langToggle = onLangChange && (
    <div role="group" aria-label="Language" className="inline-flex items-center rounded-full border bg-card p-0.5 text-xs font-medium">
      {(["en", "de"] as const).map((code) => (
        <button key={code} type="button" onClick={() => onLangChange(code)} aria-pressed={lang === code} className={`min-h-9 rounded-full px-3 transition-colors ${lang === code ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );

  return (
    <header className="sticky top-0 z-30 border-b bg-background/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-4 sm:gap-4 sm:px-5 lg:px-8">
        <Link to={sitePath("home", lang)} aria-label="Phil's Conversation" className="shrink-0">
          <img src="/logo.webp" alt="Phil's Conversation" width={140} height={40} className="site-nav-logo h-8 w-auto sm:h-9" />
        </Link>
        <nav aria-label="Main" className="hidden items-center gap-5 xl:gap-7 lg:flex">
          {deskGroup(l.profiles, current === "profiles", gapItems)}
          {deskLink("program")}
          {deskLink("stories")}
          {deskGroup(l.read, current === "insights" || current === "podcast", readItems)}
        </nav>
        <div className="flex items-center gap-1 sm:gap-3">
          <a href={CLIENT_LOGIN_URL} className="site-nav-link hidden text-sm font-medium text-muted-foreground hover:text-foreground xl:inline"><span className="site-nav-label">{l.login}</span></a>
          {langToggle}
          <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0 lg:hidden" aria-label={lang === "de" ? "Coaching-Guide öffnen" : "Open Coaching Guide"} title={lang === "de" ? "Coaching-Guide" : "Coaching Guide"} onClick={() => window.dispatchEvent(new Event(OPEN_COACHING_GUIDE_EVENT))}><MessageCircleQuestion className="h-5 w-5" /></Button>
          <Button asChild size="sm" className={`site-nav-cta hidden lg:inline-flex ${pressable}`}><Link to={expressionCheckPath(lang)} onClick={trackBookingCta}>{l.book}</Link></Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10 lg:hidden" aria-label={l.menu}><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-sm overflow-y-auto">
              <SheetTitle className="sr-only">{l.menu}</SheetTitle>
              <nav aria-label="Main" className="mt-10 space-y-1">
                {mLink(sitePath("home", lang), l.home, current === "home")}
                {mLink(SITE_PATHS.profiles[lang], l.profiles, current === "profiles")}
                {mLink(SUB_PATHS.precision[lang], l.precision, false, true)}
                {mLink(SUB_PATHS.confidence[lang], l.confidence, false, true)}
                {mLink(SUB_PATHS.quiz[lang], l.quiz, false, true)}
                {mLink(sitePath("program", lang), l.program, current === "program")}
                {mLink(sitePath("stories", lang), l.stories, current === "stories")}
                {mLink(sitePath("insights", lang), l.insights, current === "insights")}
                {mLink(sitePath("podcast", lang), l.podcast, current === "podcast")}
              </nav>
              <a href={CLIENT_LOGIN_URL} className="mt-4 block rounded-lg px-3 py-3 text-base font-medium text-muted-foreground hover:bg-muted">{l.login}</a>
              <Button asChild size="lg" className={`site-nav-cta mt-8 w-full ${pressable}`}><Link to={expressionCheckPath(lang)} onClick={() => { setOpen(false); trackBookingCta(); }}>{l.book}</Link></Button>
              <p className="mt-3 px-3 text-center text-xs leading-relaxed text-muted-foreground">{l.bookNote}</p>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
